// src/pages/HelpCenterPage.jsx
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { FaSearch, FaQuestionCircle, FaPlayCircle } from "react-icons/fa";

const COLOR = {
  primary: "#0f172a",
  accent: "#22c55e",
  bg: "linear-gradient(180deg, #f9fafb 0%, #eef2ff 100%)",
  card: "rgba(255, 255, 255, 0.96)",
  line: "rgba(148, 163, 184, 0.35)",
  text: "#0f172a",
  muted: "#6b7280",
};

const sections = [
  {
    title: "Getting Started",
    icon: "🚀",
    faqs: [
      {
        q: "How do I add my first table?",
        a: "Go to Add Table in the sidebar and fill in the details, then start taking orders from the tables list.",
      },
      {
        q: "How to connect Bluetooth printer?",
        a: "Open Connect Bluetooth from the sidebar, enable Bluetooth on your device, pair your printer, and tap 'Test print'.",
      },
    ],
  },
  {
    title: "Billing & KOT",
    icon: "🧾",
    faqs: [
      {
        q: "KOT not printing?",
        a: "Ensure the printer is paired and paper is loaded. Then retry from KOT Management screen and check Wi-Fi/Bluetooth.",
      },
      {
        q: "Edit or cancel a bill?",
        a: "Open Sales List, pick the bill, and use edit/cancel based on your permissions.",
      },
    ],
  },
  {
    title: "Inventory & Reports",
    icon: "📊",
    faqs: [
      {
        q: "Stock in vs usage?",
        a: "Stock In records new stock received; Usage deducts consumed items. Check both under Inventory pages.",
      },
      {
        q: "Exports showing 0?",
        a: "Confirm date filters and restaurant login. Re-run report and then export CSV/PDF.",
      },
    ],
  },
];

const HelpCenterPage = () => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sections;
    const result = sections
      .map((s) => ({
        ...s,
        faqs: s.faqs.filter(
          (f) =>
            f.q.toLowerCase().includes(term) || f.a.toLowerCase().includes(term)
        ),
      }))
      .filter((s) => s.faqs.length);

    return result;
  }, [search]);

  const hasResults = filtered.some((s) => s.faqs.length);

  return (
    <Page>
      <Hero>
        <HeroLeft>
          <HeroBadge>Help & Support</HeroBadge>
          <HeroTitle>Help Center</HeroTitle>
          <HeroSub>
            Find quick answers, learn how features work, and resolve common
            issues for the web app. This mirrors the mobile help experience.
          </HeroSub>
          <TagRow>
            <Tag>Setup</Tag>
            <Tag>Billing</Tag>
            <Tag>Printer</Tag>
            <Tag>Reports</Tag>
          </TagRow>
        </HeroLeft>

        <HeroRight>
          <SearchBox>
            <FaSearch />
            <input
              placeholder="Search articles, e.g. 'printer', 'KOT'..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchBox>
          <HeroHint>
            Tip: Type a keyword like <strong>Bluetooth</strong> or{" "}
            <strong>bill</strong>.
          </HeroHint>
        </HeroRight>
      </Hero>

      {!hasResults && search.trim() && (
        <EmptyState>
          <EmptyIcon>
            <FaQuestionCircle />
          </EmptyIcon>
          <h3>No results found</h3>
          <p>
            We couldn&apos;t find any answers for{" "}
            <strong>&quot;{search.trim()}&quot;</strong>. Try another keyword or
            browse the sections below.
          </p>
        </EmptyState>
      )}

      <Grid>
        {filtered.map((section) => (
          <Card key={section.title}>
            <CardHead>
              <Emoji>{section.icon}</Emoji>
              <div>
                <h3>{section.title}</h3>
                <CardSub>Common questions & quick steps</CardSub>
              </div>
            </CardHead>
            <FaqList>
              {section.faqs.map((f) => (
                <Faq key={f.q}>
                  <div className="q">
                    <FaQuestionCircle /> {f.q}
                  </div>
                  <div className="a">{f.a}</div>
                </Faq>
              ))}
            </FaqList>
          </Card>
        ))}
      </Grid>

      <ResourceBar>
        <ResourceCard>
          <IconCircle>
            <FaPlayCircle />
          </IconCircle>
          <div>
            <strong>How-to videos</strong>
            <small>Watch short walkthroughs for setup & daily use.</small>
          </div>
          <LinkBtn
            href="#"
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            Coming soon
          </LinkBtn>
        </ResourceCard>
        <ResourceCard>
          <IconCircle>
            <FaQuestionCircle />
          </IconCircle>
          <div>
            <strong>Need more help?</strong>
            <small>Start a chat with the support team.</small>
          </div>
          <PrimaryLink as="a" href="/support">
            Go to chat
          </PrimaryLink>
        </ResourceCard>
      </ResourceBar>
    </Page>
  );
};

export default HelpCenterPage;

// === Styled components ===

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 20px;
  display: grid;
  gap: 18px;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 1024px) {
    padding: 28px 32px;
  }
`;

const Hero = styled.section`
  background: radial-gradient(
    circle at top left,
    #1f2937 0%,
    #020617 45%,
    #020617 100%
  );
  color: #e5e7eb;
  border-radius: 18px;
  padding: 22px 20px;
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1.2fr);
  gap: 20px;
  box-shadow: 0 22px 60px rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.2);
  align-items: center;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: -40%;
    background: radial-gradient(
      circle at top right,
      rgba(34, 197, 94, 0.12),
      transparent 55%
    );
    opacity: 0.75;
    pointer-events: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 18px 16px;
  }
`;

const HeroLeft = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 10px;
`;

const HeroRight = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 8px;
  justify-items: flex-start;

  @media (max-width: 900px) {
    justify-items: stretch;
  }
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #cbd5f5;
  background: rgba(15, 23, 42, 0.7);
  width: fit-content;
`;

const HeroTitle = styled.h1`
  margin: 2px 0 4px 0;
  font-size: 1.7rem;
  font-weight: 900;
  letter-spacing: -0.03em;

  @media (min-width: 1024px) {
    font-size: 1.9rem;
  }
`;

const HeroSub = styled.p`
  margin: 0;
  opacity: 0.9;
  max-width: 600px;
  font-size: 0.94rem;
  line-height: 1.5;
`;

const TagRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
`;

const Tag = styled.span`
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.7);
  font-weight: 700;
  font-size: 11px;
  color: #e5e7eb;
`;

const SearchBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.98);
  color: ${COLOR.text};
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.45);
  min-width: 260px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  transition: box-shadow 0.15s ease, transform 0.15s ease,
    border-color 0.15s ease;

  svg {
    opacity: 0.8;
  }

  &:focus-within {
    box-shadow: 0 18px 45px rgba(34, 197, 94, 0.45);
    border-color: ${COLOR.accent};
    transform: translateY(-1px);
  }

  @media (max-width: 900px) {
    width: 100%;
  }

  input {
    border: 0;
    outline: none;
    background: transparent;
    width: 100%;
    font-size: 0.92rem;
    color: ${COLOR.text};

    &::placeholder {
      color: #9ca3af;
    }
  }
`;

const HeroHint = styled.div`
  font-size: 0.8rem;
  color: #cbd5f5;

  strong {
    color: #e5e7eb;
  }
`;

const Grid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px 14px 12px 14px;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.08);
  transition: transform 0.12s ease, box-shadow 0.12s ease,
    border-color 0.12s ease;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.12);
    border-color: rgba(148, 163, 184, 0.8);
  }
`;

const CardHead = styled.header`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: ${COLOR.text};
  }
`;

const CardSub = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${COLOR.muted};
  margin-top: 2px;
`;

const Emoji = styled.span`
  font-size: 24px;
`;

const FaqList = styled.div`
  display: grid;
  gap: 8px;
`;

const Faq = styled.div`
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 12px;
  padding: 9px 10px;
  background: rgba(249, 250, 251, 0.9);
  transition: background-color 0.12s ease, border-color 0.12s ease,
    transform 0.08s ease;

  .q {
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 6px;
    color: ${COLOR.text};
    font-size: 0.9rem;

    svg {
      font-size: 0.82rem;
      color: ${COLOR.accent};
    }
  }

  .a {
    color: ${COLOR.muted};
    margin-top: 5px;
    line-height: 1.5;
    font-size: 0.86rem;
  }

  &:hover {
    background: #f9fafb;
    border-color: rgba(148, 163, 184, 0.8);
    transform: translateY(-1px);
  }
`;

const ResourceBar = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
`;

const ResourceCard = styled.div`
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  color: ${COLOR.text};
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);

  small {
    color: ${COLOR.muted};
    font-size: 0.8rem;
  }
`;

const IconCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(37, 99, 235, 0.12);
  color: ${COLOR.primary};
  display: grid;
  place-items: center;
  font-size: 18px;
`;

const LinkBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  padding: 7px 11px;
  border-radius: 999px;
  border: 1px dashed rgba(148, 163, 184, 0.9);
  color: ${COLOR.muted};
  text-decoration: none;
  font-weight: 700;
  background: #f9fafb;
  font-size: 0.8rem;
  cursor: default;
`;

const PrimaryLink = styled(LinkBtn)`
  border-style: solid;
  border-color: ${COLOR.primary};
  color: #f9fafb;
  background: linear-gradient(135deg, #0f172a, #111827);
  cursor: pointer;

  &:hover {
    opacity: 0.94;
    transform: translateY(-1px);
  }
`;

const EmptyState = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  border: 1px dashed rgba(148, 163, 184, 0.8);
  padding: 18px 16px;
  display: grid;
  gap: 6px;
  justify-items: flex-start;
  color: ${COLOR.text};

  h3 {
    margin: 0;
    font-size: 0.98rem;
    font-weight: 700;
  }

  p {
    margin: 0;
    font-size: 0.86rem;
    color: ${COLOR.muted};
    max-width: 520px;
    line-height: 1.5;
  }
`;

const EmptyIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  display: grid;
  place-items: center;
  font-size: 14px;
  color: ${COLOR.muted};
`;
